// =============================================================================
// Deterministic parcelization engine.
//
// Turns a list of order/fulfilment line items into a valid multi-parcel plan,
// driven entirely by carrier/service constraints. Postage mathematics is pure,
// deterministic code — never AI, never provider-specific branching in the
// checkout path.
//
// Canonical shape (matches the persisted model):
//   Order → Fulfilment(s) → Parcel Plan → Parcel 1..N → per-parcel quote
//
// Parcelization runs independently per origin (fulfilment location), then the
// per-origin carrier rates are combined into one customer-facing price.
// =============================================================================

export const VOLUMETRIC_DIVISOR_DEFAULT = 5000; // cm³ → kg (air/express)

/**
 * Carrier/service parcel constraints. Every field is optional; an absent
 * limit means "no configured limit for that dimension". These are configuration
 * data, not logic — a future provider self-describes by declaring them.
 */
export interface ParcelConstraintSet {
  /** Maximum gross parcel weight in kg (e.g. Australia Post domestic = 22). */
  maxWeightKg?: number | null;
  /** Maximum single dimension in cm (length / width / height). */
  maxLengthCm?: number | null;
  /** Maximum girth in cm: longest side + 2 × (other two sides). */
  maxGirthCm?: number | null;
  /** Maximum bounding-box volume in cm³. */
  maxVolumeCm3?: number | null;
  /** Maximum number of units permitted in a single parcel. */
  maxPackageCapacity?: number | null;
  /** Packaging tare (empty box/parcel) weight added to every parcel. */
  tareWeightKg?: number | null;
  /** Packing-material (void fill / dunnage) allowance added to every parcel. */
  packingMaterialAllowanceKg?: number | null;
  /** Divisor for volumetric weight (cm³ → kg). */
  volumetricDivisor?: number | null;
  /** Whether chargeable weight = max(gross, volumetric). */
  dimensionalWeightEnabled?: boolean;
}

/**
 * Carrier-specific *defaults* (configuration, not logic). The engine never
 * branches on carrier; these defaults are merged into a service's own
 * constraints and can always be overridden per service/account.
 */
export const CARRIER_PARCEL_CONSTRAINT_DEFAULTS: Record<string, ParcelConstraintSet> = {
  // Australia Post domestic parcels: 22 kg physical max, 105 cm single dimension.
  auspost: {
    maxWeightKg: 22,
    maxLengthCm: 105,
    volumetricDivisor: VOLUMETRIC_DIVISOR_DEFAULT,
    dimensionalWeightEnabled: true,
  },
  startrack: {
    maxWeightKg: 30,
    maxLengthCm: 105,
    volumetricDivisor: VOLUMETRIC_DIVISOR_DEFAULT,
    dimensionalWeightEnabled: true,
  },
  sendle: {
    maxWeightKg: 25,
    maxLengthCm: 105,
    volumetricDivisor: VOLUMETRIC_DIVISOR_DEFAULT,
    dimensionalWeightEnabled: true,
  },
  // International express carriers: dimensional weight only by default; their
  // exact per-account weight/size caps are configured per connected account.
  dhl: { volumetricDivisor: VOLUMETRIC_DIVISOR_DEFAULT, dimensionalWeightEnabled: true },
  fedex: { volumetricDivisor: VOLUMETRIC_DIVISOR_DEFAULT, dimensionalWeightEnabled: true },
  ups: { volumetricDivisor: VOLUMETRIC_DIVISOR_DEFAULT, dimensionalWeightEnabled: true },
};

/** A single order line item to pack (per-unit weight/dimensions). */
export interface ParcelItemInput {
  id: string;
  quantity: number;
  weightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  /** Optional fulfilment origin; used to split parcelization per location. */
  originLocationId?: string | null;
}

export interface PackedItem {
  id: string;
  quantity: number;
}

export interface Parcel {
  parcelNumber: string;
  deadWeightKg: number;
  tareWeightKg: number;
  materialAllowanceKg: number;
  grossWeightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  volumeCm3: number;
  volumetricWeightKg: number;
  chargeableWeightKg: number;
  units: number;
  items: PackedItem[];
}

export interface ParcelizationFailure {
  itemId: string;
  reason: string;
}

export type ParcelizationStrategy = "safest" | "lowest_cost";

export interface ParcelPlan {
  ok: boolean;
  parcels: Parcel[];
  /** Items that could not be packed into any valid parcel (never fabricate). */
  failures: ParcelizationFailure[];
  totalGrossWeightKg: number;
  totalChargeableWeightKg: number;
  parcelCount: number;
  strategy: ParcelizationStrategy;
}

/** A single physical unit (expanded from quantity) for packing granularity. */
interface PackUnit {
  id: string;
  weightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function positive(value: number | null | undefined): number | null {
  const n = Number(value ?? 0);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Resolve the effective constraint set: carrier defaults merged with overrides. */
export function resolveParcelConstraints(
  carrierCode?: string | null,
  overrides?: ParcelConstraintSet | null,
): ParcelConstraintSet {
  const defaults = (carrierCode && CARRIER_PARCEL_CONSTRAINT_DEFAULTS[carrierCode]) || {};
  return { ...defaults, ...(overrides ?? {}) };
}

function divisorOf(constraints: ParcelConstraintSet): number {
  return positive(constraints.volumetricDivisor) ?? VOLUMETRIC_DIVISOR_DEFAULT;
}

function tareOf(constraints: ParcelConstraintSet): number {
  return positive(constraints.tareWeightKg) ?? 0;
}

function materialOf(constraints: ParcelConstraintSet): number {
  return positive(constraints.packingMaterialAllowanceKg) ?? 0;
}

function dimensionalEnabled(constraints: ParcelConstraintSet): boolean {
  return constraints.dimensionalWeightEnabled !== false;
}

/** Volumetric weight of a bounding box (cm³ → kg). */
export function volumetricWeightKg(volumeCm3: number, divisor: number = VOLUMETRIC_DIVISOR_DEFAULT): number {
  if (volumeCm3 <= 0) return 0;
  return round2(volumeCm3 / divisor);
}

/** Chargeable weight = max(gross weight, volumetric weight). */
export function chargeableWeightKg(
  grossWeightKg: number,
  volumeCm3: number,
  constraints: ParcelConstraintSet,
): number {
  if (!dimensionalEnabled(constraints)) return round2(grossWeightKg);
  const vol = volumetricWeightKg(volumeCm3, divisorOf(constraints));
  return round2(Math.max(grossWeightKg, vol));
}

function girthCm(l: number, w: number, h: number): number {
  return l + 2 * (w + h);
}

/** Reasons a single unit can never fit any parcel (hard carrier limits). */
function unitFailureReason(unit: PackUnit, constraints: ParcelConstraintSet): string | null {
  const maxWeight = positive(constraints.maxWeightKg);
  const maxLength = positive(constraints.maxLengthCm);
  const maxGirth = positive(constraints.maxGirthCm);
  const tare = tareOf(constraints);
  const material = materialOf(constraints);

  if (maxWeight != null) {
    const gross = unit.weightKg + tare + material;
    const vol = unit.lengthCm * unit.widthCm * unit.heightCm;
    if (chargeableWeightKg(gross, vol, constraints) > maxWeight) {
      return `item exceeds maximum parcel weight of ${maxWeight} kg`;
    }
  }
  if (maxLength != null) {
    const longest = Math.max(unit.lengthCm, unit.widthCm, unit.heightCm);
    if (longest > maxLength) {
      return `item exceeds maximum dimension of ${maxLength} cm`;
    }
  }
  if (maxGirth != null && girthCm(unit.lengthCm, unit.widthCm, unit.heightCm) > maxGirth) {
    return `item exceeds maximum girth of ${maxGirth} cm`;
  }
  return null;
}

/** Whether a parcel can accept one more unit without breaching any constraint. */
function canFit(parcel: Parcel, unit: PackUnit, constraints: ParcelConstraintSet): boolean {
  const maxWeight = positive(constraints.maxWeightKg);
  const maxLength = positive(constraints.maxLengthCm);
  const maxGirth = positive(constraints.maxGirthCm);
  const maxVolume = positive(constraints.maxVolumeCm3);
  const maxCapacity = positive(constraints.maxPackageCapacity);
  const tare = tareOf(constraints);
  const material = materialOf(constraints);

  const dead = parcel.deadWeightKg + unit.weightKg;
  const gross = dead + tare + material;

  const length = Math.max(parcel.lengthCm, unit.lengthCm);
  const width = Math.max(parcel.widthCm, unit.widthCm);
  // Stacking model: heights accumulate, other dims take the max.
  const height = parcel.heightCm + unit.heightCm;
  const volume = length * width * height;

  if (maxWeight != null && gross > maxWeight) return false;
  if (maxLength != null) {
    if (length > maxLength || width > maxLength || height > maxLength) return false;
  }
  if (maxGirth != null && girthCm(length, width, height) > maxGirth) return false;
  if (maxVolume != null && volume > maxVolume) return false;
  if (maxCapacity != null && parcel.units + 1 > maxCapacity) return false;

  if (maxWeight != null && chargeableWeightKg(gross, volume, constraints) > maxWeight) return false;
  return true;
}

function finalizeParcel(parcelNumber: string, items: PackedItem[], units: PackUnit[], constraints: ParcelConstraintSet): Parcel {
  const tare = tareOf(constraints);
  const material = materialOf(constraints);
  const deadWeightKg = round2(units.reduce((s, u) => s + u.weightKg, 0));
  const lengthCm = units.reduce((m, u) => Math.max(m, u.lengthCm), 0);
  const widthCm = units.reduce((m, u) => Math.max(m, u.widthCm), 0);
  const heightCm = round2(units.reduce((s, u) => s + u.heightCm, 0));
  const volumeCm3 = round2(lengthCm * widthCm * heightCm);
  const grossWeightKg = round2(deadWeightKg + tare + material);
  const volumetric = volumetricWeightKg(volumeCm3, divisorOf(constraints));
  const chargeable = chargeableWeightKg(grossWeightKg, volumeCm3, constraints);

  return {
    parcelNumber,
    deadWeightKg,
    tareWeightKg: round2(tare),
    materialAllowanceKg: round2(material),
    grossWeightKg,
    lengthCm: round2(lengthCm),
    widthCm: round2(widthCm),
    heightCm,
    volumeCm3,
    volumetricWeightKg: volumetric,
    chargeableWeightKg: chargeable,
    units: units.length,
    items,
  };
}

/** Pack units in the given order via first-fit-decreasing. */
function packUnits(ordered: PackUnit[], constraints: ParcelConstraintSet): { parcels: Parcel[]; failures: ParcelizationFailure[] } {
  const parcels: Parcel[] = [];
  const failures: ParcelizationFailure[] = [];
  // Track units per parcel as we go (expanded form, replaced on finalize).
  const bucketUnits: PackUnit[][] = [];
  const bucketItems: PackedItem[][] = [];

  for (const unit of ordered) {
    const hardFailure = unitFailureReason(unit, constraints);
    if (hardFailure) {
      failures.push({ itemId: unit.id, reason: hardFailure });
      continue;
    }

    let placed = false;
    for (let i = 0; i < parcels.length; i++) {
      if (canFit(parcels[i], unit, constraints)) {
        bucketUnits[i].push(unit);
        const existing = bucketItems[i];
        const item = existing.find((it) => it.id === unit.id);
        if (item) item.quantity += 1;
        else existing.push({ id: unit.id, quantity: 1 });
        parcels[i] = finalizeParcel(parcels[i].parcelNumber, bucketItems[i], bucketUnits[i], constraints);
        placed = true;
        break;
      }
    }

    if (!placed) {
      const number = `PKG-${String(parcels.length + 1).padStart(3, "0")}`;
      bucketUnits.push([unit]);
      bucketItems.push([{ id: unit.id, quantity: 1 }]);
      parcels.push(finalizeParcel(number, bucketItems[bucketItems.length - 1], [unit], constraints));
    }
  }

  return { parcels, failures };
}

function expandUnits(items: ParcelItemInput[]): PackUnit[] {
  const units: PackUnit[] = [];
  for (const item of items) {
    const qty = Math.max(0, Math.floor(Number(item.quantity ?? 0)));
    for (let i = 0; i < qty; i++) {
      units.push({
        id: item.id,
        weightKg: Number(item.weightKg ?? 0),
        lengthCm: Number(item.lengthCm ?? 0),
        widthCm: Number(item.widthCm ?? 0),
        heightCm: Number(item.heightCm ?? 0),
      });
    }
  }
  return units;
}

function compareUnitsDesc(a: PackUnit, b: PackUnit): number {
  if (a.weightKg !== b.weightKg) return b.weightKg - a.weightKg;
  const av = a.lengthCm * a.widthCm * a.heightCm;
  const bv = b.lengthCm * b.widthCm * b.heightCm;
  if (av !== bv) return bv - av;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function planFromParcels(
  parcels: Parcel[],
  failures: ParcelizationFailure[],
  strategy: ParcelizationStrategy,
): ParcelPlan {
  const totalGrossWeightKg = round2(parcels.reduce((s, p) => s + p.grossWeightKg, 0));
  const totalChargeableWeightKg = round2(parcels.reduce((s, p) => s + p.chargeableWeightKg, 0));
  return {
    ok: failures.length === 0 && parcels.length > 0,
    parcels,
    failures,
    totalGrossWeightKg,
    totalChargeableWeightKg,
    parcelCount: parcels.length,
    strategy,
  };
}

/**
 * Build a deterministic multi-parcel plan from line items.
 *
 * Strategy handling: the engine evaluates two deterministic orderings
 * (weight-descending and volume-descending) and keeps the plan with the fewest
 * parcels (the cost proxy); ties break on the lower total chargeable weight.
 * "safest" additionally prefers the plan whose heaviest single parcel is
 * lightest, so no parcel sits close to a hard carrier limit.
 */
export function buildParcelPlan(
  items: ParcelItemInput[],
  constraints: ParcelConstraintSet,
  strategy: ParcelizationStrategy = "safest",
): ParcelPlan {
  const units = expandUnits(items);
  if (units.length === 0) {
    return planFromParcels([], [], strategy);
  }

  const orderings: PackUnit[][] = [
    [...units].sort(compareUnitsDesc), // weight-desc
    [...units].sort((a, b) => {
      const av = a.lengthCm * a.widthCm * a.heightCm;
      const bv = b.lengthCm * b.widthCm * b.heightCm;
      if (av !== bv) return bv - av;
      if (a.weightKg !== b.weightKg) return b.weightKg - a.weightKg;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    }), // volume-desc
  ];

  let best: ParcelPlan | null = null;
  for (const ordering of orderings) {
    const { parcels, failures } = packUnits(ordering, constraints);
    const plan = planFromParcels(parcels, failures, strategy);
    if (!best) {
      best = plan;
      continue;
    }
    if (plan.failures.length < best.failures.length) {
      best = plan;
    } else if (plan.failures.length === best.failures.length) {
      if (plan.parcelCount < best.parcelCount) {
        best = plan;
      } else if (plan.parcelCount === best.parcelCount) {
        const maxChargeOf = (p: ParcelPlan) => p.parcels.reduce((m, x) => Math.max(m, x.chargeableWeightKg), 0);
        const pickLower =
          strategy === "safest"
            ? maxChargeOf(plan) < maxChargeOf(best)
            : plan.totalChargeableWeightKg < best.totalChargeableWeightKg;
        if (pickLower) best = plan;
      }
    }
  }

  return best ?? planFromParcels([], [], strategy);
}

/** Split items by fulfilment origin so parcelization runs independently per location. */
export function groupItemsByOrigin(items: ParcelItemInput[]): Map<string, ParcelItemInput[]> {
  const map = new Map<string, ParcelItemInput[]>();
  for (const item of items) {
    const key = item.originLocationId ?? "";
    const list = map.get(key) ?? [];
    list.push(item);
    map.set(key, list);
  }
  return map;
}

export interface OriginPlan {
  originLocationId: string;
  plan: ParcelPlan;
}

/** Parcelize independently per origin, then combine into one customer-facing price. */
export function buildPlansByOrigin(
  items: ParcelItemInput[],
  constraints: ParcelConstraintSet,
  strategy: ParcelizationStrategy = "safest",
): OriginPlan[] {
  const groups = groupItemsByOrigin(items);
  return Array.from(groups.entries()).map(([originLocationId, groupItems]) => ({
    originLocationId,
    plan: buildParcelPlan(groupItems, constraints, strategy),
  }));
}

/** Internal rate band (mirrors carrier_service_rates). */
export interface InternalRateBand {
  baseCost: number;
  perKgCost: number;
}

/**
 * Estimate a combined internal rate by rating every parcel and summing — the
 * same canonical parcel model used by checkout and warehouse label creation.
 */
export function estimateInternalRate(parcels: Parcel[], band: InternalRateBand): number {
  const base = Number(band.baseCost ?? 0);
  const perKg = Number(band.perKgCost ?? 0);
  return round2(parcels.reduce((sum, p) => sum + base + perKg * Math.max(p.chargeableWeightKg, 0.01), 0));
}